import { combineData, externalData, type ObservableValue } from '@amazon/vinyl-observable'
import type { Maybe } from '@amazon/vinyl-util'
import { back, navigateTo, onNavigate } from '@/router/router'
import { reportClick } from '@/metrics/uiMetrics'
import type { PlaylistEntry } from '@/playback/PlaylistController'
import { isPodcastEntry } from '@/playback/playbackEntryKind'

export const NOW_PLAYING_PATH = '/now-playing'

export function isNowPlayingPath(path: string): boolean {
    return path === NOW_PLAYING_PATH || path.startsWith(NOW_PLAYING_PATH + '/')
}

/** Reactive `true` while the now-playing page is the current route. */
export const nowPlayingRouteShown$: ObservableValue<boolean> = externalData(
    isNowPlayingPath(window.location.pathname),
    (setData) => {
        setData(isNowPlayingPath(window.location.pathname))
        return onNavigate((event) => setData(isNowPlayingPath(event.current)))
    }
)

export function openNowPlaying(): void {
    if (!isNowPlayingPath(window.location.pathname)) navigateTo(NOW_PLAYING_PATH)
}

export function reportNowPlayingCollapse(): void {
    reportClick({
        actionType: 'nowPlayingStageCollapse',
        contentSrc: 'client',
        contentName: 'nowPlayingStage',
    })
}

/**
 * Collapses now-playing back to what the user was browsing, playback untouched.
 * The router's back() falls back to home on a deep link with no in-app history.
 */
export function closeNowPlaying(): void {
    reportNowPlayingCollapse()
    back()
}

/** An audio podcast episode, which now-playing presents on its own stage and transport (GH's Stage). */
export function isPodcastStageEntry(entry: Maybe<PlaylistEntry>): boolean {
    return isPodcastEntry(entry) && entry?.video !== true
}

/** Reactive `true` while the now-playing page shows the podcast stage in place of the transport bar. */
export function podcastStageShown$(
    current$: ObservableValue<PlaylistEntry | null>
): ObservableValue<boolean> {
    return combineData({ route: nowPlayingRouteShown$, entry: current$ }).map(
        ({ route, entry }) => route && isPodcastStageEntry(entry)
    )
}
